import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Calendar, Clock, FileText, QrCode, ExternalLink, Download, User } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import toast from 'react-hot-toast';
import { createOpenHome, updateOpenHome } from '../../../api/inspection';
import api from '../../../api';
import TimeInput from './TimeInput';
import DateInput from '../../../components/common/DateInput';
import AgentMultiSelect from './AgentMultiSelect';
import { reportCalendarSync } from '../utils/calendarSync';
import {
  melbourneWallTimeToUtcISOString,
  formatMelbourneDatePart,
  formatMelbourneTimePart,
} from '../../../utils/melbourneTime';
import { schedulableProperties } from '../utils/schedulableProperties';

/**
 * Modal for the "Create open home" flow (Phase 1 of the spec), extended to
 * also handle editing (Monday 12731324906 - "Open Homes" property tab needs
 * schedule/edit) and to capture who's hosting: Primary Agent (single) +
 * Secondary Agents (multi), matching Property's own agent/co_agent tiers.
 *
 * Mounts on the Property → Inspections tab (and the new Open Homes tab).
 * After a successful CREATE, shows the QR code so the agent can print it for
 * signage, plus the public check-in URL - an EDIT just closes on success
 * (there's no new session/QR to show, it's the same one). The caller is
 * responsible for refreshing its inspection list.
 */
const DEFAULT_FORM = {
  scheduled_date: '',
  scheduled_time: '',
  end_time: '',
  document_ids: [],
  notes: '',
};

// scheduled_date + duration_minutes -> Melbourne "HH:mm" end time, for
// pre-filling the edit form (the model has no end_time field of its own).
// Must resolve in Melbourne time, not the editor's browser timezone, or an
// edit opened from a non-Melbourne machine would re-save the time shifted.
function endTimeFromDuration(scheduledDate, durationMinutes) {
  const endInstant = new Date(new Date(scheduledDate).getTime() + (durationMinutes || 30) * 60000);
  return formatMelbourneTimePart(endInstant.toISOString());
}


export default function CreateOpenHomeModal({ propertyId, propertyTitle, properties, agents = [], inspection = null, onClose, onCreated, onUpdated }) {
  const isEditing = !!inspection;
  const [selectedPropertyId, setSelectedPropertyId] = useState(propertyId || inspection?.property || '');
  const [form, setForm] = useState(() => {
    if (!inspection) return { ...DEFAULT_FORM };
    return {
      ...DEFAULT_FORM,
      scheduled_date: formatMelbourneDatePart(inspection.scheduled_date),
      scheduled_time: formatMelbourneTimePart(inspection.scheduled_date),
      end_time: endTimeFromDuration(inspection.scheduled_date, inspection.duration_minutes),
      notes: inspection.notes || '',
      document_ids: (inspection.documents || []).map((d) => (typeof d === 'object' ? d.id : d)),
    };
  });
  const [primaryAgentId, setPrimaryAgentId] = useState(inspection?.agent ? String(inspection.agent) : '');
  const [secondaryAgentIds, setSecondaryAgentIds] = useState(
    (inspection?.secondary_agents_detail || []).map((a) => a.id)
  );
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const activePropertyId = propertyId || selectedPropertyId;

  // Only live On Market / Off Market listings can have an open time put out —
  // withdrawn, sold, leased and appraisal-stage properties are not for sale with
  // us any more (CRM-2026-00079). The current selection is kept regardless so
  // the edit form can still open on its own property; see the helper.
  const selectableProperties = useMemo(
    () => schedulableProperties(properties, selectedPropertyId),
    [properties, selectedPropertyId]
  );

  useEffect(() => {
    if (!activePropertyId) { setDocuments([]); return; }
    setDocsLoading(true);
    api.get(`property-management/properties/${activePropertyId}/documents/`)
      .then((res) => setDocuments(Array.isArray(res.data) ? res.data : res.data?.results || []))
      .catch(() => setDocuments([]))
      .finally(() => setDocsLoading(false));
  }, [activePropertyId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleDoc = (id) => setForm((f) => ({
    ...f,
    document_ids: f.document_ids.includes(id)
      ? f.document_ids.filter((d) => d !== id)
      : [...f.document_ids, id],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activePropertyId) {
      toast.error('Pick a property first.');
      return;
    }
    if (!form.scheduled_date || !form.scheduled_time || !form.end_time) {
      toast.error('Pick a date, start time and end time first.');
      return;
    }
    if (form.scheduled_time === form.end_time) {
      toast.error('End time must be after start time.');
      return;
    }
    setSaving(true);
    try {
      // Interpret the typed date/time as Melbourne wall-clock time (the agency's
      // timezone), NOT the editor's own browser timezone. Storing off the
      // browser meant a Manila-based agent's "11am" was saved as 1pm Melbourne,
      // which is what REA and the buyer portal (both Melbourne) then published.
      const startIso = melbourneWallTimeToUtcISOString(form.scheduled_date, form.scheduled_time);
      let endMs = new Date(melbourneWallTimeToUtcISOString(form.scheduled_date, form.end_time)).getTime();
      // An end time earlier than the start (e.g. 11pm -> 1am) means the open
      // home spans midnight, not that it's invalid - equal times are caught
      // above, so any remaining "not after" case here really is next-day.
      if (endMs <= new Date(startIso).getTime()) endMs += 24 * 60 * 60 * 1000;
      const durationMinutes = Math.round((endMs - new Date(startIso).getTime()) / 60000);
      const payload = {
        property: activePropertyId,
        scheduled_date: startIso,
        duration_minutes: durationMinutes,
        agent: primaryAgentId || undefined,
        secondary_agent_ids: secondaryAgentIds,
        // sale_method intentionally omitted: the backend derives it from the
        // property's listing sale method (see OpenHome create serializer).
        document_ids: form.document_ids,
        notes: form.notes,
        status: 'scheduled',
      };
      if (isEditing) {
        const res = await updateOpenHome(inspection.id, payload);
        toast.success('Open home updated.');
        reportCalendarSync(res.data);
        onUpdated?.(res.data);
        onClose();
      } else {
        const res = await createOpenHome(payload);
        setCreated(res.data);
        toast.success('Open home created.');
        reportCalendarSync(res.data);
        onCreated?.(res.data);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || `Could not ${isEditing ? 'update' : 'create'} the open home.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{isEditing ? 'Edit open home' : 'Create open home'}</h2>
            {propertyTitle && <p className="text-xs text-gray-500 mt-0.5">{propertyTitle}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {created ? (
          <CreatedView created={created} onClose={onClose} />
        ) : (
          <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
            {!propertyId && (
              <Labeled label="Property">
                <select
                  value={selectedPropertyId}
                  onChange={(e) => setSelectedPropertyId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                  required
                >
                  <option value="">Select a property…</option>
                  {selectableProperties.map((p) => (
                    <option key={p.id} value={p.id}>{p.address || p.title || `Property #${p.id}`}</option>
                  ))}
                </select>
              </Labeled>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Labeled label="Date" icon={<Calendar className="w-3.5 h-3.5" />}>
                <DateInput value={form.scheduled_date}
                  onChange={(v) => setForm((f) => ({ ...f, scheduled_date: v }))}
                  min={new Date().toISOString().split('T')[0]} />
              </Labeled>
              <Labeled label="Start time" icon={<Clock className="w-3.5 h-3.5" />}>
                <TimeInput
                  value={form.scheduled_time}
                  onChange={set('scheduled_time')}
                  required
                  focusRingClass="focus:ring-[#4EADB7]"
                />
              </Labeled>
              <Labeled label="End time" icon={<Clock className="w-3.5 h-3.5" />}>
                <TimeInput
                  value={form.end_time}
                  onChange={set('end_time')}
                  required
                  focusRingClass="focus:ring-[#4EADB7]"
                />
              </Labeled>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Labeled label="Primary agent" icon={<User className="w-3.5 h-3.5" />}>
                <select value={primaryAgentId} onChange={(e) => setPrimaryAgentId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm">
                  <option value="">Select agent…</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email}
                    </option>
                  ))}
                </select>
              </Labeled>
              <Labeled label="Secondary agents">
                <AgentMultiSelect
                  agents={agents}
                  value={secondaryAgentIds}
                  onChange={setSecondaryAgentIds}
                  excludeIds={primaryAgentId ? [primaryAgentId] : []}
                />
              </Labeled>
            </div>

            <Labeled label="Documents to release for this open home" icon={<FileText className="w-3.5 h-3.5" />}>
              <div className="border border-gray-200 rounded-md max-h-44 overflow-y-auto">
                {docsLoading && <div className="p-3 text-xs text-gray-500">Loading…</div>}
                {!docsLoading && documents.length === 0 && (
                  <div className="p-3 text-xs text-gray-500">
                    No documents uploaded for this property yet.
                  </div>
                )}
                {documents.map((doc) => (
                  <label key={doc.id} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={form.document_ids.includes(doc.id)} onChange={() => toggleDoc(doc.id)} />
                    <span className="text-xs text-gray-700 truncate">{doc.name || doc.category}</span>
                    <span className="ml-auto text-[10px] text-gray-400 uppercase">{doc.category}</span>
                  </label>
                ))}
              </div>
            </Labeled>

            <Labeled label="Notes (internal)">
              <textarea rows={2} value={form.notes} onChange={set('notes')}
                className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm" />
            </Labeled>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border rounded-md">Cancel</button>
              <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm bg-[#4EADB7] hover:bg-[#3f9ba4] disabled:opacity-60 text-white rounded-md">
                {saving ? (isEditing ? 'Saving…' : 'Creating…') : (isEditing ? 'Save changes' : 'Create open home')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function CreatedView({ created, onClose }) {
  const s3QrUrl = created.qr_code_url;        // server-rendered PNG (S3) — may be null locally
  const checkinUrl = created.checkin_url;
  const canvasRef = useRef(null);

  const downloadClientQr = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `open-home-${created.id}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="px-5 py-5 space-y-4">
      <div className="text-sm text-emerald-700 bg-emerald-50 rounded-md px-3 py-2">
        Open home created. Print the QR for signage or open the agent device link to start checking buyers in.
      </div>

      <div className="flex flex-col items-center bg-gray-50 rounded-xl p-5">
        {checkinUrl ? (
          <div ref={canvasRef} className="bg-white p-3 rounded-lg border">
            <QRCodeCanvas value={checkinUrl} size={176} level="M" includeMargin={false} />
          </div>
        ) : (
          <div className="w-44 h-44 flex items-center justify-center bg-white border rounded-lg text-xs text-gray-400">
            <QrCode className="w-10 h-10" />
          </div>
        )}
        <div className="mt-3 text-[11px] text-gray-500 break-all text-center">{checkinUrl}</div>
        <div className="mt-3 flex gap-2 flex-wrap justify-center">
          {checkinUrl && (
            <button type="button" onClick={downloadClientQr}
              className="text-xs flex items-center gap-1 px-2.5 py-1.5 border rounded-md hover:bg-white">
              <Download className="w-3.5 h-3.5" /> Download QR
            </button>
          )}
          {s3QrUrl && (
            <a href={s3QrUrl} target="_blank" rel="noreferrer"
               className="text-xs flex items-center gap-1 px-2.5 py-1.5 border rounded-md hover:bg-white">
              <ExternalLink className="w-3.5 h-3.5" /> S3 copy
            </a>
          )}
          {checkinUrl && (
            <a href={checkinUrl} target="_blank" rel="noreferrer"
               className="text-xs flex items-center gap-1 px-2.5 py-1.5 border rounded-md hover:bg-white">
              <ExternalLink className="w-3.5 h-3.5" /> Open buyer page
            </a>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <a
          href={`/open-home/${created.id}`}
          className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded-md"
        >
          Open agent device view
        </a>
        <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded-md">Done</button>
      </div>
    </div>
  );
}

function Labeled({ label, icon, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">{icon}{label}</span>
      {children}
    </label>
  );
}
